import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Send, Paperclip, Video } from "lucide-react";

export default function Chat() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [appt, setAppt] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);

  const load = async () => {
    try {
      const { data: a } = await api.get(`/appointments/${id}`);
      setAppt(a);
      const { data: m } = await api.get(`/chat/${id}`);
      setMsgs(m);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [id, user]);

  const send = async () => {
    if (!text && !file) return;
    let attachment_url = null; let attachment_type = null;
    if (file) {
      attachment_url = await new Promise((res)=>{ const r=new FileReader(); r.onload=()=>res(r.result); r.readAsDataURL(file); });
      attachment_type = file.type.startsWith("image/") ? "image" : "pdf";
    }
    await api.post("/chat", { appointment_id: id, text, attachment_url, attachment_type });
    setText(""); setFile(null);
    load();
  };

  if (!appt) return <div className="p-6" data-testid="chat-loading">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="chat-page">
      <div className="card-soft mb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">Consultation Chat</div>
            <div className="font-display font-bold text-lg">{appt.concern_category}</div>
            <div className="text-xs text-[#5A6D6D] mt-0.5">Status: <span className="chip">{appt.status}</span></div>
          </div>
          {appt.meeting_link && appt.status === "confirmed" && (
            <a href={appt.meeting_link} target="_blank" rel="noreferrer" className="btn-primary text-sm" data-testid="join-meet-btn">
              <Video size={16}/> Join {appt.consultation_type}
            </a>
          )}
        </div>
      </div>

      <div className="card-soft p-0 overflow-hidden">
        <div className="h-[55vh] overflow-y-auto p-4 space-y-3 bg-[#FAF9F6]" data-testid="chat-messages">
          {msgs.length === 0 && <div className="text-center text-sm text-[#5A6D6D] py-8">No messages yet. Start the conversation.</div>}
          {msgs.map((m) => {
            const mine = m.sender_id === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-2xl p-3 ${mine ? 'bg-[#2D7A78] text-white' : 'bg-white border border-[#E5EBEB]'}`}>
                  <div className="text-[10px] uppercase font-bold opacity-70">{m.sender_role}</div>
                  {m.text && <div className="text-sm mt-1 whitespace-pre-wrap">{m.text}</div>}
                  {m.attachment_url && m.attachment_type === "image" && (
                    <img src={m.attachment_url} alt="attachment" className="mt-2 rounded-lg max-h-48"/>
                  )}
                  {m.attachment_url && m.attachment_type === "pdf" && (
                    <a href={m.attachment_url} download className="text-xs underline mt-1 block">Download report.pdf</a>
                  )}
                  <div className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-[#E5EBEB] p-3 flex items-center gap-2">
          <label className="cursor-pointer p-2 hover:bg-[#F0F3F3] rounded-full" data-testid="chat-attach">
            <Paperclip size={18} color="#5A6D6D"/>
            <input type="file" className="hidden" onChange={(e)=>setFile(e.target.files?.[0])}/>
          </label>
          <input value={text} onChange={(e)=>setText(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&send()} placeholder={file ? file.name : "Type message..."} className="flex-1 px-3 py-2 rounded-full border border-[#E5EBEB]" data-testid="chat-input"/>
          <button onClick={send} className="btn-primary" data-testid="chat-send"><Send size={16}/></button>
        </div>
      </div>
    </div>
  );
}
